import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { XlsxExportButton } from "@/components/ui/XlsxExportButton";
import { downloadContractorsXlsx } from "@/features/contractors/api/exportXlsx";
import { onLeaveLabel, type OnLeaveBrief } from "@/features/contractors/api/leaves";
import { PageLoader } from "@/components/ui/Spinner";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useAuthStore } from "@/store/auth";
import { formatPhone } from "@/utils/formatPhone";
import {
  EntityType,
  getContractorDisplayName,
  getContractorTaxId,
} from "@/features/contractors/utils";
import { canManageContractors, canToggleContractorActive } from "@/features/contractors/utils";
import {
  NO_GROUP_KEY,
  NO_GROUP_LABEL,
  useAgreementGroups,
  type AgreementGroupBrief,
} from "@/features/contractors/agreementGroups";
import { matchesArea, type AreaFilter, type ContractorArea } from "@/features/contractors/areaFilter";
import { AreaTreeFilter } from "@/features/contractors/components/AreaTreeFilter";
import { useVoivodeships } from "@/features/rfq/api/scopes";
import {
  WORK_TYPE_BADGE,
  WORK_TYPE_LABELS,
  WORK_TYPES,
  type WorkType,
} from "@/features/contractors/workTypes";

/** Kategoria prac z „Zakresu współpracy" firmy (RFQ). */
interface ScopeCategoryBrief {
  id: string;
  name: string;
  status: "full" | "conditional";
  /** false = kwalifikacja zawężona do `positions` (nazw pozycji kategorii). */
  all_positions: boolean;
  positions: string[];
}

interface Contractor {
  id: string;
  entity_type: EntityType;
  name: string;
  short_name: string;
  first_name?: string;
  last_name?: string;
  nip: string;
  pesel?: string;
  is_vat_payer: boolean;
  contact_email: string;
  contact_phone: string;
  registered_city: string;
  is_active: boolean;
  display_name?: string;
  tax_identifier?: string;
  tax_identifier_label?: "NIP" | "PESEL";
  /** Grupy umów ramowych zebrane z umów podwykonawcy (tylko te, które użytkownik widzi). */
  groups?: AgreementGroupBrief[];
  /** Najbliższy urlop firmy w oknie składania zamówienia (dziś + 7 dni) — plakietka „Urlop od–do". */
  on_leave?: OnLeaveBrief | null;
  /** Przyznane kategorie prac (kolejność słownika); pusta = brak zakresu współpracy. */
  scope_categories?: ScopeCategoryBrief[];
  /** Typy prowadzonych prac z kartoteki (kolejność słownika); pusta = zwykły podwykonawca. */
  work_types?: WorkType[];
  /** Obszar działania: województwa, `counties` puste = całe województwo. */
  area?: ContractorArea[];
}

const SELECT_CLS =
  "text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

/** Filtry wielokrotnego wyboru: pusty wybór = bez filtra, kilka wartości = KTÓRAKOLWIEK z nich. */
const inGroups = (c: Contractor, keys: string[]): boolean => {
  if (keys.length === 0) return true;
  const gs = c.groups ?? [];
  return keys.some((k) => (k === NO_GROUP_KEY ? gs.length === 0 : gs.some((g) => g.id === k)));
};

/**
 * Podwykonawców się nie usuwa, tylko dezaktywuje. Domyślnie lista pokazuje samych
 * aktywnych; nieaktywnych (żeby ich aktywować ponownie) filtruje CEO i dyrektor
 * techniczny — tylko im backend je zwraca.
 */
type StatusFilter = "active" | "inactive" | "all";
const matchesStatus = (c: Contractor, f: StatusFilter): boolean =>
  f === "all" || (f === "active" ? c.is_active : !c.is_active);

/** Kolumna „VAT": forma prawna + status VAT w jednym selekcie (tak jak odznaka). */
type VatFilter = "" | "vat" | "no_vat" | "individual";
const matchesVat = (c: Contractor, f: VatFilter): boolean => {
  if (!f) return true;
  if (f === "individual") return c.entity_type === "individual";
  if (c.entity_type === "individual") return false;
  return f === "vat" ? c.is_vat_payer : !c.is_vat_payer;
};

const NO_CATEGORY_KEY = "__none__";
const NO_WORK_TYPE_KEY = "__none__";

/** Filtr „Typ prac": opcje ze SŁOWNIKA (nie z wierszy), kilka = którykolwiek z nich. */
const WORK_TYPE_OPTIONS = [
  ...WORK_TYPES.map((t) => ({ value: t.value as string, label: t.label })),
  { value: NO_WORK_TYPE_KEY, label: "Bez typu prac" },
];

const inWorkTypes = (c: Contractor, keys: string[]): boolean => {
  if (keys.length === 0) return true;
  const types = c.work_types ?? [];
  return keys.some((key) => (key === NO_WORK_TYPE_KEY ? types.length === 0 : types.includes(key as WorkType)));
};

/** Plakietki typów prac — te same kolory, co na karcie i megamapie. */
function WorkTypeBadges({ types }: { types: WorkType[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span key={t} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${WORK_TYPE_BADGE[t]}`}>
          {WORK_TYPE_LABELS[t]}
        </span>
      ))}
    </div>
  );
}

const inCategories = (c: Contractor, keys: string[]): boolean => {
  if (keys.length === 0) return true;
  const cats = c.scope_categories ?? [];
  return keys.some((key) =>
    key === NO_CATEGORY_KEY ? cats.length === 0 : cats.some((k) => k.id === key),
  );
};

const inCities = (c: Contractor, cities: string[]): boolean =>
  cities.length === 0 || cities.includes((c.registered_city ?? "").trim());

/** Plakietki kategorii prac — kolorem i dopiskiem jak w sekcji „Zakres współpracy" karty. */
function ScopeCategoryBadges({ categories }: { categories: ScopeCategoryBrief[] }) {
  return (
    <div className="flex flex-wrap gap-1">
      {categories.map((k) => (
        <Badge
          key={k.id}
          color={k.status === "conditional" ? "yellow" : "green"}
          title={k.all_positions ? undefined : `tylko: ${k.positions.join(" · ")}`}
        >
          {k.name}
          {!k.all_positions && " (część)"}
          {k.status === "conditional" && " — warunkowa"}
        </Badge>
      ))}
    </div>
  );
}

/** Checkbox wiersza: zwykły klik przełącza, Shift+klik zaznacza zakres (useRowSelection). */
function RowCheckbox({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: (shiftKey: boolean) => void;
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      // Shift+klik rozciągałby przy okazji zaznaczenie tekstu na stronie.
      onMouseDown={(e) => {
        if (e.shiftKey) e.preventDefault();
      }}
      onChange={(e) => onToggle((e.nativeEvent as MouseEvent).shiftKey === true)}
      className="rounded align-middle accent-[var(--color-primary)] cursor-pointer"
    />
  );
}

function SelectAllCheckbox({
  all,
  some,
  onToggle,
}: {
  all: boolean;
  some: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = some;
  }, [some]);
  const label = all ? "Odznacz wszystkie" : "Zaznacz wszystkie";
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      title={label}
      checked={all}
      onChange={onToggle}
      className="rounded align-middle accent-[var(--color-primary)] cursor-pointer"
    />
  );
}

/** Szukajka po „Nazwa", „NIP / PESEL" i „Kontakt" — dopasowanie po fragmencie. */
const matchesQuery = (c: Contractor, q: string): boolean => {
  if (!q) return true;
  const digits = q.replace(/[\s-]/g, "");
  const hay = [
    getContractorDisplayName(c),
    c.name,
    c.short_name,
    c.first_name,
    c.last_name,
    c.contact_email,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (hay.includes(q)) return true;
  if (!digits) return false;
  return [c.nip, c.pesel, c.contact_phone]
    .filter(Boolean)
    .some((v) => String(v).replace(/[\s-]/g, "").includes(digits));
};

export function ContractorsListPage() {
  const user = useAuthStore((s) => s.user);
  // CEO / technical director by role, or the CEO-granted 'Podwykonawcy i umowy
  // ramowe' permission (mirrors backend CanManageContractors).
  const canManage = canManageContractors(user);
  const canSeeInactive = canToggleContractorActive(user);

  // /contractors/ bez parametrów zwraca samych aktywnych (karmi pickery). Kto może
  // aktywować ponownie, prosi też o nieaktywnych — osobny klucz, bo ["contractors"]
  // dzielą te pickery.
  const { data, isLoading } = useQuery({
    queryKey: canSeeInactive ? ["contractors", { include_inactive: true }] : ["contractors"],
    queryFn: () =>
      apiClient
        .get<{ data: Contractor[] }>(
          "/contractors/",
          canSeeInactive ? { params: { include_inactive: "true" } } : undefined,
        )
        .then((r) => r.data.data),
    staleTime: 5 * 60_000,
  });

  const [search, setSearch] = useState("");
  const [filterCities, setFilterCities] = useState<string[]>([]);
  const [filterVat, setFilterVat] = useState<VatFilter>("");
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterWorkTypes, setFilterWorkTypes] = useState<string[]>([]);
  const [filterGroups, setFilterGroups] = useState<string[]>([]);
  const [filterArea, setFilterArea] = useState<AreaFilter>({});
  const [filterStatus, setFilterStatus] = useState<StatusFilter>("active");

  // Miasta i kategorie prac — z samych wierszy: tylko wartości, które faktycznie występują.
  const cityOptions = useMemo(
    () =>
      [...new Set((data ?? []).map((c) => (c.registered_city ?? "").trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, "pl"))
        .map((city) => ({ value: city, label: city })),
    [data],
  );
  const categoryOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const c of data ?? []) for (const k of c.scope_categories ?? []) byId.set(k.id, k.name);
    const out = [...byId]
      .map(([value, label]) => ({ value, label }))
      .sort((x, y) => x.label.localeCompare(y.label, "pl"));
    if (out.length > 0) out.push({ value: NO_CATEGORY_KEY, label: "Bez kategorii prac" });
    return out;
  }, [data]);

  // Grupy do filtra — słownik UTWORZONYCH grup (/agreement-groups/), nie tylko te
  // widoczne na wierszach: pusta grupa też ma się dać wybrać (pokaże „brak").
  // Select znika tylko, gdy w systemie nie założono jeszcze żadnej grupy.
  const { data: allGroups } = useAgreementGroups();
  const groupFilterOptions = useMemo(() => {
    const out = (allGroups ?? [])
      .map((g) => ({ value: g.id, label: g.name }))
      .sort((x, y) => x.label.localeCompare(y.label, "pl"));
    if (out.length > 0) out.push({ value: NO_GROUP_KEY, label: NO_GROUP_LABEL });
    return out;
  }, [allGroups]);

  // Drzewko obszaru — stały słownik 16 województw z powiatami (jak na karcie firmy).
  const { data: voivodeships } = useVoivodeships();

  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      (data ?? []).filter(
        (c) =>
          matchesQuery(c, query) &&
          inCities(c, filterCities) &&
          matchesVat(c, filterVat) &&
          inCategories(c, filterCategories) &&
          inWorkTypes(c, filterWorkTypes) &&
          inGroups(c, filterGroups) &&
          matchesArea(c.area, filterArea) &&
          matchesStatus(c, filterStatus),
      ),
    [
      data, query, filterCities, filterVat, filterCategories, filterWorkTypes, filterGroups,
      filterArea, filterStatus,
    ],
  );

  // Zaznaczenie wierszy — eksport bierze zaznaczone SPOŚRÓD widocznych, w kolejności listy.
  const visibleIds = useMemo(() => filtered.map((c) => c.id), [filtered]);
  const selection = useRowSelection(visibleIds);

  const hasActiveFilters =
    !!search || filterCities.length > 0 || !!filterVat || filterCategories.length > 0 ||
    filterWorkTypes.length > 0 || filterGroups.length > 0 ||
    Object.keys(filterArea).length > 0 || filterStatus !== "active";
  const clearFilters = () => {
    setSearch("");
    setFilterCities([]);
    setFilterVat("");
    setFilterCategories([]);
    setFilterWorkTypes([]);
    setFilterGroups([]);
    setFilterArea({});
    setFilterStatus("active");
  };

  return (
    <div>
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Podwykonawcy</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
        </div>
        {canManage && (
          <Link
            to="/contractors/new"
            className="self-start inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={14} /> Nowy podwykonawca
          </Link>
        )}
      </div>
      {/* Filters — client-side, the list is fetched whole (OptInPageNumberPagination). */}
      <div className="relative mb-3">
        <Search
          size={15}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
        />
        <input
          type="text"
          placeholder="Szukaj po nazwie, skrócie, NIP / PESEL, e-mailu lub telefonie..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Szukaj podwykonawcy"
          className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {cityOptions.length > 0 && (
          <FilterMultiSelect
            label="Miasto"
            options={cityOptions}
            selected={filterCities}
            onChange={setFilterCities}
            searchable
          />
        )}
        <select
          value={filterVat}
          onChange={(e) => setFilterVat(e.target.value as VatFilter)}
          className={SELECT_CLS}
          aria-label="VAT / forma"
        >
          <option value="">VAT: wszyscy</option>
          <option value="vat">Płatnik VAT</option>
          <option value="no_vat">Bez VAT</option>
          <option value="individual">Osoba fizyczna</option>
        </select>
        <FilterMultiSelect
          label="Typ prac"
          options={WORK_TYPE_OPTIONS}
          selected={filterWorkTypes}
          onChange={setFilterWorkTypes}
        />
        {categoryOptions.length > 0 && (
          <FilterMultiSelect
            label="Kategoria prac"
            options={categoryOptions}
            selected={filterCategories}
            onChange={setFilterCategories}
            searchable
          />
        )}
        {groupFilterOptions.length > 0 && (
          <FilterMultiSelect
            label="Grupa umów"
            options={groupFilterOptions}
            selected={filterGroups}
            onChange={setFilterGroups}
            searchable
          />
        )}
        <AreaTreeFilter options={voivodeships} value={filterArea} onChange={setFilterArea} />
        {canSeeInactive && (
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
            className={SELECT_CLS}
            aria-label="Status"
          >
            <option value="active">Aktywni</option>
            <option value="inactive">Nieaktywni</option>
            <option value="all">Aktywni i nieaktywni</option>
          </select>
        )}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="!border-[var(--color-primary)] !text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]"
          >
            {/* Ś i Ć w wersalikach mono wychodzą optycznie większe od reszty słowa. */}
            <span>
              Wyczy<span className="text-[0.9em]">ść</span> filtry
            </span>
          </Button>
        )}
      </div>
      {isLoading ? (
        <PageLoader />
      ) : (data ?? []).length === 0 ? (
        <p className="text-gray-400 dark:text-gray-500 text-sm">Brak podwykonawców.</p>
      ) : filtered.length === 0 ? (
        <p className="text-gray-400 dark:text-gray-500 text-sm">Brak podwykonawców spełniających filtry.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              {/* Na telefonie nie ma nagłówka tabeli z checkboxem „wszystkie". */}
              <button
                type="button"
                onClick={selection.toggleAll}
                className="sm:hidden underline hover:text-gray-700 dark:hover:text-gray-200"
              >
                {selection.allSelected ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
              </button>
              <span>
                {selection.selectedIds.length > 0
                  ? `Zaznaczono: ${selection.selectedIds.length} z ${filtered.length}`
                  : "Zaznacz wiersze do pobrania — Shift+klik zaznacza zakres."}
              </span>
            </div>
            <XlsxExportButton
              label="Pobierz zaznaczone (.xlsx)"
              title={
                selection.selectedIds.length > 0
                  ? "Pobierz zaznaczonych podwykonawców jako arkusz Excel"
                  : "Zaznacz co najmniej jednego podwykonawcę"
              }
              count={selection.selectedIds.length}
              download={() => downloadContractorsXlsx(selection.selectedIds)}
            />
          </div>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {filtered.map((c) => {
              const taxId = getContractorTaxId(c);
              return (
              <div
                key={c.id}
                className={`px-4 py-3 flex items-start justify-between gap-3 ${selection.isSelected(c.id) ? "bg-[var(--color-primary-dim)]" : ""}`}
              >
                <div className="pt-0.5">
                  <RowCheckbox
                    label={`Zaznacz ${getContractorDisplayName(c)}`}
                    checked={selection.isSelected(c.id)}
                    onToggle={(shift) => selection.toggle(c.id, shift)}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <Link to={`/contractors/${c.id}`} className="font-medium text-[var(--color-primary)] hover:underline block truncate">
                    {getContractorDisplayName(c)}
                  </Link>
                  {c.short_name && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{c.short_name}</p>}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">{taxId.label}: {taxId.value || "—"}</p>
                  {c.registered_city && <p className="text-xs text-gray-400 dark:text-gray-500">{c.registered_city}</p>}
                  {(c.contact_email || c.contact_phone) && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {c.contact_email}{c.contact_email && c.contact_phone ? " · " : ""}{formatPhone(c.contact_phone)}
                    </p>
                  )}
                  {(c.groups ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(c.groups ?? []).map((g) => (
                        <Badge key={g.id} color="blue">{g.name}</Badge>
                      ))}
                    </div>
                  )}
                  {(c.work_types ?? []).length > 0 && (
                    <div className="mt-1">
                      <WorkTypeBadges types={c.work_types ?? []} />
                    </div>
                  )}
                  {(c.scope_categories ?? []).length > 0 && (
                    <div className="mt-1">
                      <ScopeCategoryBadges categories={c.scope_categories ?? []} />
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <Badge color={c.is_active ? "green" : "gray"}>{c.is_active ? "Aktywny" : "Nieaktywny"}</Badge>
                  {c.on_leave && (
                    <Badge color="orange" title="Urlop firmy w oknie składania zamówienia — na listach i w podpowiedzi stoi na dole z tym dopiskiem">
                      {onLeaveLabel(c.on_leave)}
                    </Badge>
                  )}
                  {c.entity_type === "individual" ? (
                    <Badge color="purple">Osoba fizyczna</Badge>
                  ) : (
                    <Badge color={c.is_vat_payer ? "blue" : "gray"}>{c.is_vat_payer ? "VAT" : "Bez VAT"}</Badge>
                  )}
                </div>
              </div>
              );
            })}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="w-10 px-3 py-2 text-center">
                    <SelectAllCheckbox
                      all={selection.allSelected}
                      some={selection.someSelected}
                      onToggle={selection.toggleAll}
                    />
                  </th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Nazwa</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">NIP / PESEL</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Miasto</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kontakt</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">VAT</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Typy prac</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kategorie prac</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => {
                  const taxId = getContractorTaxId(c);
                  return (
                  <tr
                    key={c.id}
                    className={
                      selection.isSelected(c.id)
                        ? "bg-[var(--color-primary-dim)]"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    }
                  >
                    <td className="w-10 px-3 py-3 text-center">
                      <RowCheckbox
                        label={`Zaznacz ${getContractorDisplayName(c)}`}
                        checked={selection.isSelected(c.id)}
                        onToggle={(shift) => selection.toggle(c.id, shift)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/contractors/${c.id}`} className="font-medium text-[var(--color-primary)] hover:underline">
                        {getContractorDisplayName(c)}
                      </Link>
                      {c.short_name && (
                        <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">{c.short_name}</span>
                      )}
                      {c.entity_type === "individual" && (
                        <span className="ml-2 text-xs text-purple-600 dark:text-purple-300">UZ/UoD</span>
                      )}
                      {(c.groups ?? []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(c.groups ?? []).map((g) => (
                            <Badge key={g.id} color="blue">{g.name}</Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono text-xs">
                      <span className="text-[10px] text-gray-400 mr-1">{taxId.label}:</span>{taxId.value || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{c.registered_city ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {c.contact_email && <span className="block text-xs">{c.contact_email}</span>}
                      {c.contact_phone && <span className="block text-xs text-gray-400 dark:text-gray-500">{formatPhone(c.contact_phone)}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {c.entity_type === "individual" ? (
                        <Badge color="purple">Osoba fizyczna</Badge>
                      ) : (
                        <Badge color={c.is_vat_payer ? "blue" : "gray"}>{c.is_vat_payer ? "Płatnik VAT" : "Bez VAT"}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <Badge color={c.is_active ? "green" : "gray"}>{c.is_active ? "Aktywny" : "Nieaktywny"}</Badge>
                        {c.on_leave && (
                          <Badge color="orange" title="Urlop firmy w oknie składania zamówienia — na listach i w podpowiedzi stoi na dole z tym dopiskiem">
                            {onLeaveLabel(c.on_leave)}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(c.work_types ?? []).length > 0 ? (
                        <WorkTypeBadges types={c.work_types ?? []} />
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500" aria-label="bez typu prac">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(c.scope_categories ?? []).length > 0 ? (
                        <ScopeCategoryBadges categories={c.scope_categories ?? []} />
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
